import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Plus, Trash2, Repeat, Loader2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FREQ_LABELS = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };
const fmt = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

export default function RecurringEntryForm() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', memo: '', frequency: 'monthly', start_date: '', end_date: '',
    auto_post: false,
    lines: [
      { account_id: '', description: '', debit: 0, credit: 0 },
      { account_id: '', description: '', debit: 0, credit: 0 },
    ],
  });

  useEffect(() => {
    if (!wsId) return;
    apiFetch(`${API_URL}/api/workspaces/${wsId}/accounts`)
      .then(r => r.json())
      .then(d => setAccounts(d.accounts || d || []))
      .catch(() => {});
  }, [wsId]);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { account_id: '', description: '', debit: 0, credit: 0 }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, val) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [field]: val };
    setForm({ ...form, lines });
  };

  const totalDebit = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!isBalanced) { toast.error('Debits must equal credits'); return; }
    if (!form.start_date) { toast.error('Start date required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, lines: form.lines.map(l => ({ ...l, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 })) };
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/recurring-entries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Recurring entry created');
        navigate('/recurring-entries');
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to create');
      }
    } catch {
      toast.error('Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="recurring-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/recurring-entries')} data-testid="recurring-form-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Repeat className="w-6 h-6" /> New Recurring Journal Entry
          </h1>
          <p className="text-sm text-muted-foreground">Set up a journal entry that repeats on a schedule.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Template Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Monthly Rent" data-testid="rec-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="rec-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQ_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Memo</Label>
            <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="Optional memo for generated entries" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Start Date *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="rec-start" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">End Date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Post Mode</Label>
              <div className="flex items-center gap-2 h-10 px-2 rounded-lg border bg-muted/30">
                <Switch checked={form.auto_post} onCheckedChange={(v) => setForm({ ...form, auto_post: v })} data-testid="rec-auto-post" />
                <span className="text-sm">{form.auto_post ? 'Auto-Post' : 'Create as Draft'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Journal Lines</Label>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Line
            </Button>
          </div>
          <div className="space-y-2">
            {form.lines.map((line, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 sm:col-span-4">
                  {i === 0 && <Label className="text-[10px]">Account</Label>}
                  <Select value={line.account_id} onValueChange={(v) => updateLine(i, 'account_id', v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select account…" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map(a => (
                        <SelectItem key={a.account_id} value={a.account_id}>
                          <span className="text-xs">{a.code} - {a.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 sm:col-span-3">
                  {i === 0 && <Label className="text-[10px]">Description</Label>}
                  <Input className="h-9 text-xs" value={line.description} onChange={(e) => updateLine(i, 'description', e.target.value)} placeholder="Description" />
                </div>
                <div className="col-span-5 sm:col-span-2">
                  {i === 0 && <Label className="text-[10px]">Debit</Label>}
                  <Input className="h-9 text-xs font-mono" type="number" min="0" step="0.01" value={line.debit} onChange={(e) => updateLine(i, 'debit', e.target.value)} data-testid={`rec-line-debit-${i}`} />
                </div>
                <div className="col-span-5 sm:col-span-2">
                  {i === 0 && <Label className="text-[10px]">Credit</Label>}
                  <Input className="h-9 text-xs font-mono" type="number" min="0" step="0.01" value={line.credit} onChange={(e) => updateLine(i, 'credit', e.target.value)} data-testid={`rec-line-credit-${i}`} />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  {form.lines.length > 2 && (
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => removeLine(i)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="grid grid-cols-12 gap-2 pt-2 border-t font-mono text-xs">
              <div className="col-span-7 text-right font-medium">Totals:</div>
              <div className={cn('col-span-2 font-bold', !isBalanced && 'text-red-600')}>{fmt(totalDebit)}</div>
              <div className={cn('col-span-2 font-bold', !isBalanced && 'text-red-600')}>{fmt(totalCredit)}</div>
              <div className="col-span-1">{isBalanced && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-card/95 backdrop-blur border-t px-4 sm:px-0 py-3 flex justify-end gap-3 z-10">
        <Button variant="outline" onClick={() => navigate('/recurring-entries')} data-testid="recurring-form-cancel-btn">Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving || !isBalanced}
          className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[180px]"
          data-testid="save-recurring-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Repeat className="w-4 h-4 mr-2" />}
          Create Recurring Entry
        </Button>
      </div>
    </div>
  );
}
